/**
 * GeneralSelectInput Component
 *
 * A reusable Formik-integrated select input component using react-select.
 * Supports async pagination, infinite scrolling, default values, and custom styling.
 *
 * Props:
 * @param {string} name - Formik field name.
 * @param {string} label - Optional label for the select input.
 * @param {boolean} required - Whether the field is required. Default: false.
 * @param {Array<{ value: any; label: string }>} options - Select options array.
 * @param {string} placeholder - Placeholder text. Default: "".
 * @param {string} wrapperClassName - Extra CSS classes for the wrapper container.
 * @param {boolean} disabled - Disable the select input. Default: false.
 * @param {{ value: any; label: string }} defaultValue - Initial default value.
 * @param {(selectedOption: any) => void} onChange - Optional onChange handler.
 * @param {() => void} fetchNextPage - Function to fetch next page of options (for infinite scroll).
 * @param {boolean} hasNextPage - Whether there are more pages to fetch.
 * @param {(inputValue: string) => void} onInputChange - Optional input change handler.
 * @param {() => void} onBlur - Optional blur handler.
 * @param {{ value: any; label: string }} value - Controlled value for the select input.
 *
 * Behavior:
 * - Fully integrated with Formik: updates field value and marks touched state.
 * - Supports infinite scrolling: fetchNextPage is called when user scrolls to bottom.
 * - Handles default values and controlled values.
 * - Provides custom styling for react-select (transparent background, no borders/shadows, small padding).
 * - Renders validation messages from Formik when field is touched and has an error.
 * - Menu portal is attached to document body to avoid clipping issues.
 * - Menu placement is automatic.
 *
 * Usage:
 * ```tsx
 * <Formik initialValues={{ warehouse: "" }} onSubmit={...}>
 *   {() => (
 *     <Form>
 *       <GeneralSelectInput
 *         name="warehouse"
 *         label="Select Warehouse"
 *         placeholder="Choose warehouse"
 *         options={[{ value: "1", label: "Warehouse A" }, { value: "2", label: "Warehouse B" }]}
 *         fetchNextPage={loadMoreWarehouses}
 *         hasNextPage={true}
 *       />
 *     </Form>
 *   )}
 * </Formik>
 * ```
 *
 * Notes:
 * - Uses react-select's built-in menu portal to render dropdown in body, preventing overflow issues.
 * - Infinite scroll triggered both by inView hook and onMenuScrollToBottom for safety.
 * - Fully compatible with Formik validation and error handling.
 */

import { Field, FieldProps, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import Select from "react-select";
import { GeneralSelectInputProps } from "../../types/GeneralComponents";

const GeneralSelectInput: React.FC<GeneralSelectInputProps> = ({
  name,
  label,
  required = false,
  options,
  placeholder = "",
  wrapperClassName,
  disabled,
  defaultValue,
  onChange,
  fetchNextPage,
  hasNextPage,
  onInputChange,
  onBlur,
  value,
}) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { inView } = useInView();

  useEffect(() => {
    if (defaultValue && defaultValue.value !== null) {
      setFieldValue(name, defaultValue.value);
    }
  }, []);

  const handleChange = (selectedOption: any) => {
    setFieldValue(name, selectedOption?.value || "");

    if (onChange) {
      onChange(selectedOption);
    }
  };
  //?========== Fetch more items when last item is in view===============
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);
  //?===============Custom Select Styles =============================
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      border: "none",
      backgroundColor: "transparent",
      boxShadow: "none",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0px",
    }),
    input: (provided: any) => ({
      ...provided,
      margin: "0px",
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      padding: "0px",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: "0px",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "gray",
      fontSize: "14px",
    }),
  };

  return (
    <div className={`${wrapperClassName} relative w-full`}>
      {label && (
        <label htmlFor={name}>
          {label}
          {required && "*"}
        </label>
      )}
      <Field name={name}>
        {({ field, meta }: FieldProps) => {
          return (
            <div className="flex w-full flex-col gap-2">
              <div className="rounded-lg border border-inputBorder">
                <Select
                  id={name}
                  name={field.name}
                  options={options}
                  value={value}
                  onChange={handleChange}
                  isDisabled={disabled}
                  placeholder={placeholder}
                  defaultValue={defaultValue}
                  classNamePrefix="react-select"
                  styles={customStyles}
                  onInputChange={onInputChange}
                  onMenuClose={() => setFieldTouched(name, true)}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  onBlur={onBlur}
                  // ref={infiniteScrollRef}
                  onMenuScrollToBottom={async (e) => {
                    if (!hasNextPage || isLoading) return;
                    const target = e.target as HTMLDivElement;
                    const atBottom =
                      target.scrollTop + target.clientHeight >=
                      target.scrollHeight - 10;
                    if (atBottom) {
                      setIsLoading(true);
                      await fetchNextPage();
                      setIsLoading(false);
                    }
                  }}
                  className={`focus:ring-primary h-full w-full rounded-lg bg-inputBg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 ${
                    meta.touched && meta.error ? "border-errorLighter" : ""
                  }`}
                />
              </div>
              {meta.error && meta.touched && (
                <div className="flex items-center gap-1">
                  <p className="text-sm font-semibold text-errorMain">
                    {meta.error}
                  </p>
                </div>
              )}
            </div>
          );
        }}
      </Field>
    </div>
  );
};

export default GeneralSelectInput;
